// 2) Third-party libraries
import { fetchAuthSession } from "@aws-amplify/auth";
import {
    find,
    filter as lFilter,
    get,
    isEmpty,
    isUndefined,
    map,
    orderBy,
} from "lodash-es";
import { all, call, delay, fork, put, select, takeLatest } from "redux-saga/effects";

// 3) Local imports
// 3.1 Constants
import { API_NAME, maxAPIRefetchCount, refetchAPIDelay } from "../../config/config";
import { DETAILS_TAB, TASKS_PAGE, TASK_HISTORY_PAGE } from "../../config/tableAndPageConstants";
import { FamilyNameAttribute, GivenNameAttribute } from "../../constants/authUserAttributes";
import { compactViewPages } from "../../constants/common";
import { fetchPaymentPlanScheduleHandler } from "../../constants/paymentPlansSortAndFilters";
import { taskHistoryBatchTypes } from "../../constants/tasksSortAndFilters";

// 3.3 Project-specific modules � components/types/other
import dayjs from "src/config/dayjs";
import { appliedFilterIndicator } from "../../constants/filterbar";
import { _queries as queries } from "../../graphql/queries.graphql";
import { PageData } from "../common/types";
import { UserPreference } from "../users/types";
import client, { post } from "src/api";
import {
    SetSelectedTaskHistoryPayload,
    Task,
    TasksActionTypes,
    TasksFetchedData,
} from "./types";

// 3.4 Utility functions / types
import {
    checkShouldRequestRefetch,
    customPostData,
    getRegionallyGraphqlResponse,
    formatDateToDateObjectUTC,
    getGraphqlQueryString,
    removeAppliedFiltersForApiRequest,
    getRegionallyApiResponse,
    populateSortFieldsParamSortParams,
} from "../../utils/commonFunctions";
import {
    checkIfEmailIsValid,
    getIsCompactViewForPage,
} from "../../utils/commonFunctions";
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { ApplicationState } from "../storeTypes";
import { getUsingCustomerWorflow, getCloudImportType } from "../companies/companySlice";
import { getCurrentUser } from "../auth/authSlice";
import { getUserPreferencesRequestAction } from "../users/userSlice";
import { getOrganisationActionUrl } from "../organisations/organisationSlice";
import {
    initialState,
    bulkSaveTaskDetailsUpdateResponseAction,
    getTableCompactViewRequestAction,
    getTableCompactViewSuccessAction,
    getTaskDataErrorAction,
    getTaskDataSuccessAction,
    getTaskHistoryChangedTasksDataErrorAction,
    getTaskHistoryChangedTasksDataSuccessAction,
    getTaskHistoryChangedTasksErrorAction,
    getTaskHistoryChangedTasksSuccessAction,
    getTaskHistoryCollectionsDataErrorAction,
    getTaskHistoryCollectionsDataSuccessAction,
    getTaskHistoryCollectionsTaskDataErrorAction,
    getTaskHistoryCollectionsTaskDataSuccessAction,
    getTaskHistoryCollectionTasksErrorAction,
    getTaskHistoryCollectionTasksSuccessAction,
    getTaskHistoryDataErrorAction,
    getTaskHistoryDataSuccessAction,
    getTaskHistoryErrorAction,
    getTaskHistoryNotificationDataErrorAction,
    getTaskHistoryNotificationDataRequestAction,
    getTaskHistoryNotificationDataSuccessAction,
    getTaskHistoryNotificationInvoicesErrorAction,
    getTaskHistoryNotificationInvoicesSuccessAction,
    getTaskHistoryNotificationsErrorAction,
    getTaskHistoryNotificationsSuccessAction,
    getTaskHistoryNotificationTasksErrorAction,
    getTaskHistoryNotificationTasksSuccessAction,
    getTaskHistoryRequestAction,
    getTaskHistorySuccessAction,
    getTaskHistoryTasksFailedErrorAction,
    getTaskHistoryTasksFailedSuccessAction,
    getTaskPaymentPlanScheduleErrorAction,
    getTaskPaymentPlanScheduleSuccessAction,
    getTasksActionFilterOptionsRequestAction,
    getTasksActionFilterOptionsResponseAction,
    getTasksErrorAction,
    getTasksRequestAction,
    getTasksSuccessAction,
    getTaskTicketInvoicesSuccessAction,
    notifyCustomersResponseAction,
    processTaskCompactViewReduxSettingAction,
    setSelectedTaskHistoryCollectionsTaskIdSuccessAction,
    setSelectedTaskHistoryIdSuccessAction,
    setSelectedTaskHistoryNotificationIdSuccessAction,
    setSelectedTaskIdSuccessAction,
    setTableCompactViewSuccessAction,
    setTaskExpandStatusAction,
    getTaskHistoryDeliveryReportErrorAction,
    getTaskHistoryDeliveryReportSuccessAction,
    getTaskViewerDetailForUserSuccessAction,
    getTaskViewerDetailForUserRequestAction,
    getTaskViewerDetailForUserErrorAction,
    getTaskWorkflowsSuccessAction,
    getTaskWorkflowsRequestAction,
    getTaskWorkflowsErrorAction,
    getTaskViewerDetailForUserOrganisationRequestAction,
    getTasksForOrganisationRequestAction,
    tasksFetchedForOrganisationAction,
    getOrganisationTaskHistoryNotificationDataSuccessAction,
    getOrganisationTaskHistoryNotificationDataErrorAction,
    getOrganisationTasksHistoryRequestAction,
    getActionedPhoneCallDataRequestAction,
    taskActionOrganisationPhoneCallTaskRequest,
    taskActionPhoneCallTaskRequest,
    getTaskInvoiceCustomFieldsForCompanyRequestAction,
    getTaskInvoiceCustomFieldsForOrganisationRequestAction,
    getTaskCustomerCustomFieldsForCompanyRequestAction,
    getTaskCustomerCustomFieldsForOrganisationRequestAction,
    deleteTaskViewingsRequestAction,
    addTaskViewingsRequestAction,
    taskOrganisationPaymentBehaviorInsightsRequestAction,
    taskPaymentBehaviorInsightsRequestAction,
    getActionedPaymentBehaviourInsightRequestAction,
    getRemittanceAdvicesRequestAction,
    actionOrganisationManualAllocatePaymentRequest,
    actionManualAllocatePaymentRequest,
    getTriggersForWorkflowRequestAction,
    taskActionOrganisationRequestRATaskRequest,
    taskActionRequestRATaskRequest,
    taskActionOrganisationPaymentRALinkTaskRequest,
    taskActionPaymentRALinkTaskRequest,
    taskActionOrganisationPaymentVerificationTaskRequest,
    taskActionPaymentVerificationTaskRequest,
    taskAddCommentRequestAction,
    setTableCompactViewRequestAction,
    organisationEscalateToCollectionsAction,
    escalateToCollectionsAction,
    getTaskHistoryCollectionsTaskDataRequestAction,
    setSelectedTaskHistoryCollectionsTaskIdRequestAction,
    getTaskHistoryCollectionTasksRequestAction,
    getTaskHistoryCollectionsDataRequestAction,
    getActionedPaymentDetailsRequestAction,
    getActionedPaymentPlanDetailsRequestAction,
    getActionedTicketDetailsRequestAction,
    getWorkflowStepOptionsRequestAction,
    getTaskEditPropertiesRequestAction,
    organisationBulkSaveTaskDetailsUpdateRequestAction,
    bulkSaveTaskDetailsUpdateRequestAction,
    getTaskHistoryTasksFailedRequestAction,
    getTaskHistoryChangedTasksRequestAction,
    getTaskHistoryChangedTasksDataRequestAction,
    getTaskHistoryActionedTasksRequestAction,
    getTaskHistoryActionedTasksDataRequestAction,
    downloadMergedPdfStatusRequestAction,
    downloadMergedPdfRequestAction,
    getTaskHistoryBatchStatusRequestAction,
    getTaskHistoryQueuedTasksCountRequestAction,
    getTaskHistoryNotificationTasksRequestAction,
    getTaskHistoryNotificationInvoicesRequestAction,
    getOrganisationTaskHistoryNotificationInvoicesRequestAction,
    getOrganisationTaskHistoryNotificationDataRequestAction,
    getTaskHistoryDeliveryReportRequestAction,
    getTaskHistoryNotificationsRequestAction,
    setSelectedTaskHistoryNotificationIdRequestAction,
    setSelectedTaskHistoryIdRequestAction,
    getTaskHistoryDataRequestAction,
    getTaskPaymentPlanScheduleRequestAction,
    getTaskTicketInvoicesRequestAction,
    setSelectedTaskIdRequestAction,
    getTaskDataForOrganisationRequestAction,
    getTaskDataRequestAction,
    notifyOrganisationCustomersRequestAction,
    notifyCustomersRequestAction,
    checkTasksReadyStateActionForOrganisation,
    checkTasksReadyStateAction,
    getTasksPageData,
    getTasksData,
    getActionedCancelPaymentPlanDetailsRequestAction,
    getTaskHistoryCancelledPaymentPlanTasksSuccessAction,
    getTaskHistoryCancelledPaymentPlanTasksErrorAction,
    getActionTaskOptionsRequestAction,
    taskActionCancelPaymentPlanRequestAction
} from "./taskSlice";

const preparePayloadForTaskFilter = ({ payload, canExcludeTasks, parseCustomFields }: { 
    payload: any, 
    canExcludeTasks: boolean,
    parseCustomFields: boolean
 }) => {
    const {
        filter,
        filters,
        sortBy,
        sortAscending,
        usingMultipleWorkflow,
        isPaymentPlanEnabled,
        taskIds,
        excludeTasks,
        recordLimit,
        lastKeySet
    } = payload;

    const finalPayload: any = {
        Ascending: sortAscending,
        ...(
            canExcludeTasks ? {
                TaskIds: taskIds,
                ExcludeTasks: excludeTasks,
                RecordLimit: recordLimit
            } : {}
        ),
        ...(lastKeySet !== undefined ? { LastKeySet: lastKeySet } : {})
    };
    const finalFilter = filter || filters; // Handle different filter property names in models

    if (finalFilter) {
        const usedFilters = {
            ...finalFilter,
            Type: finalFilter.Type,
            [`Type${appliedFilterIndicator}`]: finalFilter.Type,
        };
        const cleanFilters = removeAppliedFiltersForApiRequest(
            usedFilters,
            true,
            'task',
            true
        );
        const assignedUserValue = cleanFilters.AssignedUserId;

        if (assignedUserValue) {
            if (checkIfEmailIsValid(assignedUserValue)) {
                delete cleanFilters.AssignedUserId;
                cleanFilters.AssignedEmailAddress = assignedUserValue;
            }
        }

        if (parseCustomFields) {
            if (cleanFilters.CustomFieldFilters) {
                cleanFilters.CustomFieldFilters = JSON.parse(
                    cleanFilters.CustomFieldFilters
                );
            }

            if (cleanFilters.CustomFieldMultipleValuesFilters) {
                cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
                    cleanFilters.CustomFieldMultipleValuesFilters
                );
            }
        }

        if (!usingMultipleWorkflow) {
            delete cleanFilters.WorkflowIds;
        }

        Object.assign(finalPayload, cleanFilters);
    }

    if (sortBy) {
        const sortFields = populateSortFieldsParamSortParams(sortBy);

        if (parseCustomFields && sortFields.CustomFieldSort) {
            sortFields.CustomFieldSort = JSON.parse(
                sortFields.CustomFieldSort
            );
        }

        Object.assign(finalPayload, sortFields);
    }

    return finalPayload;
}

let refetchCount = 0;

/* Function for getting the tasks list to be used for Active tasks page.
 * @param param0
 */
function* handleGetTasksRequest({ payload: sagaPayload }: any) {
    const { ...payload } = sagaPayload;
    let { refetchCount } = sagaPayload;
    refetchCount = refetchCount || 0;
    const errorMessage = 'Error fetching task list. Please try again later.';

    try {
        const {
            pageSize,
            currentPage,
            lockedDeliveryMethod,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            isPaymentPlanEnabled,
        } = payload;

        const cloudImportType: string = yield select(getCloudImportType);

        // To call async functions, use redux-saga's `call()`.
        const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: false, parseCustomFields: false });

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASKS_FOR_COMPANY,
                variables: {
                    ...commonTaskFilter,
                    PageSize: pageSize,
                    Skip: currentPage * TASKS_PAGE.pageSize,
                    LockedDeliveryMethod: lockedDeliveryMethod,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    IsMultipleWorkflow: usingMultipleWorkflow,
                    PaymentPlanEnabled: isPaymentPlanEnabled,
                }
            }
        );
        
        const responseData = get(res.data, 'GetTasksForCompany');
        const { Tasks = [], LastKeySet = undefined } = responseData || {};

        if (Tasks && Tasks.length >= 0) {
            const responsePayload = {
                data: Tasks,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Tasks.length < pageSize) &&
                        !(pageSize < TASKS_PAGE.pageSize),
                },
                lastKeySet: LastKeySet,
            };

            refetchCount = 0;
            yield put(getTasksSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTasksRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getTasksErrorAction([errorMessage]));
        }
    }
}

function* handleGetTasksForOrganisationRequest({ payload: sagaPayload }: any) {
    const { ...payload } = sagaPayload;
    let { refetchCount } = sagaPayload;
    refetchCount = refetchCount || 0;
    const errorMessage = 'Error fetching task list. Please try again later.';

    try {
        const {
            pageSize,
            currentPage,
            lockedDeliveryMethod,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            isPaymentPlanEnabled
        } = payload;

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: false, parseCustomFields: false });

        const variables = {
            ...commonTaskFilter,
            PageSize: pageSize,
            Skip: currentPage * TASKS_PAGE.pageSize,
            LockedDeliveryMethod: lockedDeliveryMethod,
            IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            IsMultipleWorkflow: usingMultipleWorkflow,
            PaymentPlanEnabled: isPaymentPlanEnabled,
        };

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASKS_FOR_ORGANISATION,
            variables
        );

        const { Tasks } = get(res.data, 'GetTasksForOrganisation');

        if (Tasks) {
            const responsePayload = {
                data: Tasks,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Tasks.length < pageSize) &&
                        !(pageSize < TASKS_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getTasksSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTasksForOrganisationRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getTasksErrorAction([errorMessage]));
        }
    }
}

function* handleGetOrganisationTasksHistoryRequest({ payload }: any) {
    const errorMessage = 'Error fetching task history list. Please try again later.';

    try {
        const { 
            filters, 
            sortBy, 
            sortAscending, 
            pageSize, 
            currentPage 
        } = payload;

        // To call async functions, use redux-saga's `call()`.
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'task',
            true
        );

        const actionedByUserValue = cleanFilters.ActionedByUserId;

        if (actionedByUserValue && checkIfEmailIsValid(actionedByUserValue)) {
            delete cleanFilters.ActionedByUserId;
            cleanFilters.ActionedByEmailAddress = actionedByUserValue;
        }

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const variables = {
            ...cleanFilters,
            SortField: sortBy,
            Ascending: sortAscending,
            PageSize: pageSize,
            Skip: currentPage * TASK_HISTORY_PAGE.pageSize,
        };

        const response: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_BATCH_HISTORY_FOR_ORGANISATION,
            variables
        );

        const { TaskBatches } = get(response.data, 'GetTaskBatchHistoryForOrganisation');

        if (TaskBatches) {
            const responsePayload = {
                data: TaskBatches,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(TaskBatches.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getTaskHistorySuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount 
                && checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getTaskHistoryRequestAction(payload));
        } else {
            yield put(getTaskHistoryErrorAction([errorMessage]));
        }
    }
}

/**
 * Function for checking the ready state of tasks selected to enable/disable the `Action` button in
 * Active tasks page.
 * @param param0
 */
function* handleCheckTasksReadyStateRequest({ payload }: any) {
    const {
        pageSize,
        currentPage,
        callback,
    } = payload;

    // To call async functions, use redux-saga's `call()`.
    const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: false });

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.CHECK_TASKS_ACTION_STATE,
                variables: {
                    ...commonTaskFilter,
                    PageSize: pageSize,
                    Skip: currentPage * TASKS_PAGE.pageSize,
                }
            }
        );

        const tasksActionState = get(res.data, 'TasksActionState');

        if (callback) {
            callback(tasksActionState);
        }
    } catch (err) {
        yield put(notifyCustomersResponseAction());

        if (callback) {
            callback(false);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for checking the ready state of tasks selected to enable/disable the `Action` button in
 * Active tasks page.
 * @param param0
 */
function* handleCheckTasksReadyStateForOrganisationRequest({ payload }: any) {
    const {
        pageSize,
        currentPage,
        callback,
    } = payload;
    
    try {
        const currentSession: DynamicObject = yield call(fetchAuthSession);
        const accessToken = get(currentSession, 'tokens.accessToken');

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        
        let res: DynamicObject;
        const usedQuery = queries.CHECK_TASKS_READY_STATE_FOR_ORGANISATION;
        const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: false });

        const variables = {
            ...commonTaskFilter,
            PageSize: pageSize,
            Skip: currentPage * TASKS_PAGE.pageSize,
        }

        if (apiUrl) {
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(usedQuery),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        } else {
            res = yield call(
                [client, 'graphql'], {
                    query: usedQuery, 
                    variables
                }
            );
        }

        const tasksActionState = get(res.data, 'TasksActionStateForOrganisation');

        if (callback) callback(tasksActionState);
    } catch (err) {
        yield put(notifyCustomersResponseAction());

        if (callback) {
            callback(false);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called when proceeding with notify customers for communication tasks.
 * @param param0
 */
function* handleNotifyCustomersRequest({ payload: sagaPayload }: any) {
    const { callback, isCommunicationSelectedByUser, selectedContact, templateName } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
        IsCommunicationSelectedByUser: isCommunicationSelectedByUser,
        SelectedContact: selectedContact,
        ...(templateName ? { TemplateName: templateName } : {})
    };

    const apiPath = templateName
        ? '/task/action/manual-communication/send'
        : '/task/action/automated-communication/send';

    try {
        yield call(post, {
            apiName: API_NAME,
            path: apiPath,
            options: {
                body: payload
            }
        });

        yield put(notifyCustomersResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        yield put(notifyCustomersResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleNotifyOrganisationCustomersRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
    };
    
    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/task/action/organisation/automated-communication/send',
            'POST',
            payload
        );

        yield put(notifyCustomersResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        yield put(notifyCustomersResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the tasks action filter options.
 */
function* handleGetTasksActionFilterOptionsRequest({ payload: sagaPayload }: any) {
    const { companyIds, isOrgView } = sagaPayload;

    try {
        let res: DynamicObject;

        if (!isOrgView) {
            // To call async functions, use redux-saga's `call()`.
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_TASKS_ACTION_FILTER_OPTIONS
                }
            );
        } else {
            const apiUrl: string | undefined = yield select(
                getOrganisationActionUrl
            );

            // To call async functions, use redux-saga's `call()`.
            res = yield call(
                getRegionallyGraphqlResponse,
                apiUrl,
                queries.GET_TASKS_ACTION_FILTER_OPTIONS, 
                { CompanyIds: companyIds || null }
            );
        }
        
        if (res.error) {
            console.log('res.error', res.error);
        } else {
            refetchCount = 0;

            yield put(
                getTasksActionFilterOptionsResponseAction(
                    res.data.GetTasksActionFilterOptions
                )
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTasksActionFilterOptionsRequestAction(companyIds, isOrgView));
        } else {
            getTasksActionFilterOptionsResponseAction({});
        }
    }
}

/**
 * Function for getting the task details for a selected task.
 * @param param0
 */
function* handleGetTaskDataRequest({ payload: sagaPayload }: any) {
    const {
        taskId,
        lockedDeliveryMethod,
        isUsingCloudImportType,
        usingMultipleWorkflow,
        isPaymentPlanEnabled,
        isCalendarView
    } = sagaPayload;

    const errorMessage = 'Error fetching task details. Please try again later.';

    try {
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_DETAILS_FOR_COMPANY,
                variables: {
                    TaskId: taskId,
                    LockedDeliveryMethod: lockedDeliveryMethod,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    IsMultipleWorkflow: usingMultipleWorkflow,
                    PaymentPlanEnabled: isPaymentPlanEnabled,
                    IsCalendarView: isCalendarView
                }
            }
        );

        const Task = get(res.data, 'GetTaskDetailsForCompany');

        if (Task) {
            const responsePayload = {
                record: Task,
            };
            
            yield put(getTaskDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskDataErrorAction([errorMessage]));
    }
}

function* handleGetTaskDataForOrganisationRequest({ payload: sagaPayload }: any) {
    const {
        taskId,
        companyId,
        lockedDeliveryMethod,
        isUsingCloudImportType,
        usingMultipleWorkflow,
        isPaymentPlanEnabled,
    } = sagaPayload;

    const errorMessage = 'Error fetching task details. Please try again later.';

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const variables = {
            TaskId: taskId,
            CompanyId: companyId,
            LockedDeliveryMethod: lockedDeliveryMethod,
            IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            IsMultipleWorkflow: usingMultipleWorkflow,
            PaymentPlanEnabled: isPaymentPlanEnabled,
        };

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_DETAILS_FOR_ORGANISATION,
            variables
        );

        const Task = get(res.data, 'GetTaskDetailsForOrganisation');

        if (Task) {
            const responsePayload = {
                record: Task,
            };

            yield put(getTaskDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskDataErrorAction([errorMessage]));
    }
}

/**
 * Function that sets the selected task id in redux.
 * @param param0
 */
function* handleSetSelectedTaskIdRequest({ payload }: any) {
    const { taskId, callback, companyId } = payload;
    yield put(setSelectedTaskIdSuccessAction(taskId, companyId));
    callback();
}

/**
 * Function for getting the ticket invoices list.
 * @param param0
 */
function* handleGetTaskTicketInvoicesRequest({ payload }: any) {
    const errorMessage = `Error fetching notification's invoice list. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            invoiceState,
            pageSize,
            currentPage,
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_INVOICES_FOR_COMPANY,
            {
                ...cleanFilters,
                InvoiceState: invoiceState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.INVOICE_LIST.pageSize,
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForCompany');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Invoices.length < pageSize) &&
                        !(pageSize < DETAILS_TAB.INVOICE_LIST.pageSize),
                },
            };

            yield put(getTaskTicketInvoicesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(
            getTaskHistoryNotificationInvoicesErrorAction([errorMessage])
        );
    }
}

/**
 * Payment Plan Data Schedule
 */
/**
 * Function that fetches the schedule for the selected payment plan task.
 * @param param0
 */
function* handleGetTaskPaymentPlanScheduleRequest({ payload }: any) {
    const errorMessage = `Error fetching payment plan's schedule list. Please try again later.`;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);

        const taskActiveData: DynamicObject = yield select(
            (state: ApplicationState) =>
                state.tasks.activeTasks.activeData.record
        );

        const paymentPlan = get(taskActiveData, 'PaymentPlan', []);

        const paymentPlanSchedules = get(
            paymentPlan,
            'PaymentPlanSchedules',
            []
        );
        const planSchedulesFiltered: any[] = fetchPaymentPlanScheduleHandler(
            paymentPlanSchedules,
            cleanFilters
        );

        if (paymentPlanSchedules) {
            const sortOrder = sortAscending ? 'asc' : 'desc';
            const paymentPlanSorted = orderBy(
                planSchedulesFiltered,
                [sortBy],
                [sortOrder]
            );

            const responsePayload = {
                data: paymentPlanSorted,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(paymentPlanSchedules.length < pageSize) &&
                        !(pageSize < DETAILS_TAB.INVOICE_LIST.pageSize),
                },
            };

            yield put(getTaskPaymentPlanScheduleSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskPaymentPlanScheduleErrorAction([errorMessage]));
    }
}

/**
 * Task History section
 */
/**
 * Function for getting the task history list.
 * @param param0
 */
function* handleGetTaskHistoryRequest({ payload }: any) {
    const errorMessage = 'Error fetching task history list. Please try again later.';

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        // To call async functions, use redux-saga's `call()`.
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'task',
            true
        );

        const actionedByUserValue = cleanFilters.ActionedByUserId;

        if (actionedByUserValue && checkIfEmailIsValid(actionedByUserValue)) {
            delete cleanFilters.ActionedByUserId;
            cleanFilters.ActionedByEmailAddress = actionedByUserValue;
        }

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_BATCH_HISTORY_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * TASK_HISTORY_PAGE.pageSize,
                }
            }
        );

        const { TaskBatches } = get(res.data, 'GetTaskBatchHistoryForCompany');

        if (TaskBatches) {
            const responsePayload = {
                data: TaskBatches,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(TaskBatches.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.pageSize),
                },
            };

            refetchCount = 0;

            yield put(getTaskHistorySuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTaskHistoryRequestAction(payload));
        } else {
            yield put(getTaskHistoryErrorAction([errorMessage]));
        }
    }
}

/**
 * Function for getting the details for a selected task history record.
 * @param param0
 */
function* handleGetTaskHistoryDataRequest({ payload: taskId }: any) {
    const errorMessage = 'Error fetching task details. Please try again later.';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_DETAILS_FOR_COMPANY,
                variables: {
                    TaskId: taskId,
                }
            }
        );

        const Task = get(res.data, 'GetTaskDetailsForCompany');

        if (Task) {
            const responsePayload = {
                record: Task,
            };

            yield put(getTaskHistoryDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

/**
 * Function for setting the id for the selected item in task history.
 * @param param0
 */
function* handleSetSelectedTaskHistoryIdRequest({ payload }: any) {
    const { BatchId, CompanyId, callback } = payload;
    const setSelectedTaskHistoryPayload: SetSelectedTaskHistoryPayload = {
        BatchId, CompanyId
    };
    yield put(setSelectedTaskHistoryIdSuccessAction(setSelectedTaskHistoryPayload));
    callback();
}

// Task History notifications
/**
 * Function for setting the selected id for the notifications under task history.
 * @param param0
 */
function* handleSetSelectedTaskHistoryNotificationIdRequest({ payload }: any) {
    const { taskId, callback } = payload;
    yield put(setSelectedTaskHistoryNotificationIdSuccessAction(taskId));

    callback();
}

/**
 * Function for getting the task history notifications list.
 * @param param0
 */
function* handleGetTaskHistoryNotificationsRequest({ payload }: any) {
    const errorMessage = `Error fetching task history's notification list. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
        } = payload;
        // To call async functions, use redux-saga's `call()`.
        const sortFields = populateSortFieldsParamSortParams(sortBy);

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'task'
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_BATCH_GROUP_HISTORY_FOR_COMPANY,
            {
                ...cleanFilters,
                ...sortFields,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * TASK_HISTORY_PAGE.notifications.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            }
        );

        const { TaskBatchGroups } = get(
            res.data,
            'GetTaskBatchGroupHistoryForCompany'
        );

        if (TaskBatchGroups) {
            const responsePayload = {
                data: TaskBatchGroups,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(TaskBatchGroups.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.notifications.pageSize),
                },
            };

            yield put(
                getTaskHistoryNotificationsSuccessAction(responsePayload)
            );
        } else {
            yield put(getTaskHistoryNotificationsErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
        // yield put(
        //     getCustomerInvoicesSuccessAction({
        //         data: initialState.activeData.invoices.data,
        //         pageData: initialState.activeData.invoices.pageData,
        //     })
        // );

        yield put(getTaskHistoryNotificationsErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the task history delivery report list.
 * @param param0
 */
 function* handleGetTaskHistoryDeliveryReportRequest({ payload }: any) {
     const errorMessage = `Error fetching task history's deliver report list. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType
        } = payload;
        // To call async functions, use redux-saga's `call()`.
        const sortFields = populateSortFieldsParamSortParams(sortBy);
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'task'
        );
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_BATCH_GROUP_DELIVERY_HISTORY_FOR_COMPANY, 
            {
                ...cleanFilters,
                ...sortFields,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * TASK_HISTORY_PAGE.notifications.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            }
        );

        const { TaskBatchGroups } = get(
            res.data,
            'GetTaskBatchGroupDeliveryHistoryForCompany'
        );

        if (TaskBatchGroups) {
            const responsePayload = {
                data: TaskBatchGroups,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(TaskBatchGroups.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.notifications.pageSize),
                },
            };

            yield put(
                getTaskHistoryDeliveryReportSuccessAction(responsePayload)
            );
        } else {
            yield put(getTaskHistoryDeliveryReportErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryDeliveryReportErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the task history notification details.
 * @param param0
 */
function* handleGetTaskHistoryNotificationDataRequest({
    payload: { 
        notificationId, 
        isUsingCloudImportType 
    },
}: any) {
    const errorMessage = 'Error fetching notification details. Please try again later.';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_COMMUNICATION_DETAIL,
                variables: {
                    CommunicationId: notificationId,
                    AllInvoices: true,
                }
            }
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const CommunicationData = get(res.data, 'GetCommunicationDetail');

        if (CommunicationData) {
            const customerId = get(CommunicationData, 'Customer.Id');

            const resCustomer: DynamicObject = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_CUSTOMER_DETAILS_FOR_COMPANY,
                    variables: {
                        CustomerId: customerId,
                        IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    }
                }
            );

            const Customer = get(
                resCustomer.data,
                'GetCustomerDetailsForCompany'
            );

            if (Customer) {
                CommunicationData.Customer = Customer;
            }

            const responsePayload = {
                record: CommunicationData,
            };

            refetchCount = 0;

            yield put(
                getTaskHistoryNotificationDataSuccessAction(responsePayload)
            );
        } else {
            yield put(
                getTaskHistoryNotificationDataErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(
                getTaskHistoryNotificationDataRequestAction(
                    notificationId,
                    isUsingCloudImportType
                )
            );
        } else {
            yield put(
                getTaskHistoryNotificationDataErrorAction([errorMessage])
            );
        }
    }
}

/**
 * Function for getting the organisation's task history notification details.
 * @param param0
 */
function* handleGetOrganisationTaskHistoryNotificationDataRequest({
    payload: { 
        companyId,
        companyIds,
        notificationId, 
        conversionCurrency,
        isUsingCloudImportType 
    },
}: any) {
    const errorMessage = 'Error fetching notification details. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_COMMUNICATION_DETAIL,
            {
                CommunicationId: notificationId,
                AllInvoices: true,
            }
        ); 
        const cloudImportType: string = yield select(getCloudImportType);

        const CommunicationData = get(res.data, 'GetCommunicationDetail');

        if (CommunicationData) {
            const customerId = get(CommunicationData, 'Customer.Id');

            const resCustomer: DynamicObject = yield call(
                getRegionallyGraphqlResponse,
                apiUrl,
                queries.GET_CUSTOMER_DETAILS_FOR_ORGANISATION,
                {
                    CompanyId: companyId,
                    CompanyIds: companyIds,
                    CustomerId: customerId,
                    ConversionCurrency: conversionCurrency,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                }
            );
            const Customer = get(
                resCustomer.data,
                'GetCustomerDetailsForOrganisation'
            );

            if (Customer) {
                CommunicationData.Customer = Customer;
            }

            const responsePayload = {
                record: CommunicationData,
            };

            refetchCount = 0;

            yield put(
                getOrganisationTaskHistoryNotificationDataSuccessAction(responsePayload)
            );
        } else {
            yield put(
                getOrganisationTaskHistoryNotificationDataErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(
                getTaskHistoryNotificationDataRequestAction(
                    notificationId,
                    isUsingCloudImportType
                )
            );
        } else {
            yield put(
                getTaskHistoryNotificationDataErrorAction([errorMessage])
            );
        }
    }
}

/**
 * Function for getting the task history notification invoices tab data.
 * @param param0
 */
function* handleGetOrganisationTaskHistoryNotificationInvoicesRequest({ payload }: any) {
    const errorMessage = `Error fetching organisation notification's invoice list. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            invoiceState,
            pageSize,
            currentPage,
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        // To call async functions, use redux-saga's `call()`.

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_INVOICES_FOR_COMPANY,
            {
                ...cleanFilters,
                InvoiceState: invoiceState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.INVOICE_LIST.pageSize,
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForOrganisation');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Invoices.length < pageSize) &&
                        !(pageSize < DETAILS_TAB.INVOICE_LIST.pageSize),
                },
            };

            yield put(
                getTaskHistoryNotificationInvoicesSuccessAction(responsePayload)
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(
            getTaskHistoryNotificationInvoicesErrorAction([errorMessage])
        );
    }
}

/**
 * Function for getting the task history notification invoices tab data.
 * @param param0
 */
function* handleGetTaskHistoryNotificationInvoicesRequest({ payload }: any) {
    const errorMessage = `Error fetching notification's invoice list. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            invoiceState,
            pageSize,
            currentPage,
        } = payload;
        
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);

        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string |undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_INVOICES_FOR_COMPANY,
            {
                ...cleanFilters,
                InvoiceState: invoiceState,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.INVOICE_LIST.pageSize,
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForCompany');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Invoices.length < pageSize) &&
                        !(pageSize < DETAILS_TAB.INVOICE_LIST.pageSize),
                },
            };

            yield put(
                getTaskHistoryNotificationInvoicesSuccessAction(responsePayload)
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(
            getTaskHistoryNotificationInvoicesErrorAction([errorMessage])
        );
    }
}

/**
 * Function for getting the task history notification tasks data.
 * @param param0
 */
function* handleGetTaskHistoryNotificationTasksRequest({ payload }: any) {
    const errorMessage = `Error fetching notification's tasks list. Please try again later.`;

    try {
        const {
            filters,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'task'
        );
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_FOR_COMPANY, {
                ...cleanFilters,
                SortField: get(payload, 'sortBy'),
                Ascending: get(payload, 'sortAscending'),
                PageSize: pageSize,
                Skip:
                    currentPage *
                    TASK_HISTORY_PAGE.notifications.tasks.pageSize,
            }
        );

        const { TaskHistories } = get(res.data, 'GetTaskHistoryForCompany');

        if (TaskHistories) {
            const responsePayload = {
                data: TaskHistories,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(TaskHistories.length < pageSize) &&
                        !(
                            pageSize <
                            TASK_HISTORY_PAGE.notifications.tasks.pageSize
                        ),
                },
            };

            yield put(
                getTaskHistoryNotificationTasksSuccessAction(responsePayload)
            );
        } else {
            yield put(
                getTaskHistoryNotificationTasksErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryNotificationTasksErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the task history queued tasks count.
 * @param param0
 */
function* handleGetTaskHistoryQueuedTasksCountRequest({ payload }: any) {
    const { batchId, companyId, callback } = payload;

    // To call async functions, use redux-saga's `call()`.
    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_QUEUED_TASKS_COUNT, 
            {
                BatchId: batchId,
                CompanyId: companyId || null
            }
        );

        const QueuedTasksCount = get(
            res.data,
            'GetTaskHistoryQueuedTasksCount'
        );

        if (callback) {
            callback({
                IsSuccess: true,
                QueuedTasksCount,
            });
        }            
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for getting the task history batch status.
 * @param param0
 */
function* handleGetTaskHistoryBatchStatusRequest({ payload }: any) {
    const { batchId, companyId, callback } = payload;

    // To call async functions, use redux-saga's `call()`.
    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_BATCH_STATUS, 
            {
                BatchId: batchId,
                CompanyId: companyId || null
            }
        );

        const TaskBatchStatus = get(res.data, 'GetTaskBatchStatus');

        if (callback)
            callback({
                IsSuccess: TaskBatchStatus ? true : false,
                ...TaskBatchStatus,
            });
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for getting the link for downloading merged pdf file in Task history notifications section.
 * @param param0
 */
function* handleDownloadMergedPdfRequest({ payload }: any) {
    const { batchId, callback } = payload;
    
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.DOWNLOAD_MERGED_PDF,
                variables: {
                    BatchId: batchId || null,
                }
            }
        );

        res.IsSuccess = true;

        if (res.data.DownloadMergePdf) {
            res.data.DownloadMergePdf = get(res, 'data.DownloadMergePdf');
        }

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for getting the button status for the download merged pdf button.
 * @param param0
 */
function* handleDownloadMergedPdfStatusRequest({ payload }: any) {
    const { batchId, companyId, callback } = payload;
    // To call async functions, use redux-saga's `call()`.
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.ENABLE_DOWNLOAD_MERGE_PDF_LINK, 
                variables: {
                    BatchId: batchId || null,
                    CompanyId: companyId || null,
                }
            }
        );

        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

// Task history actioned tasks
/**
 * Function for getting the actioned tasks details.
 * @param param0
 */
function* handleGetTaskHistoryActionedTasksDataRequest({ payload }: any) {
    const errorMessage = 'Error fetching actioned tasks details. Please try again later.';
    const { batchId, companyId } = payload;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_BATCH_HISTORY_DETAILS, 
            {
                BatchId: batchId,
                CompanyId: companyId
            }
        );

        const ActionedTasksData = get(res.data, 'GetTaskBatchHistoryDetails');

        if (ActionedTasksData) {
            const responsePayload = {
                record: ActionedTasksData,
            };

            yield put(
                getTaskHistoryChangedTasksDataSuccessAction(responsePayload)
            );
        } else {
            yield put(
                getTaskHistoryChangedTasksDataErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryChangedTasksDataErrorAction([errorMessage]));
    }
}

// Task history changed tasks
/**
 * Function for getting the changed tasks details.
 * @param param0
 */
function* handleGetTaskHistoryChangedTasksDataRequest({
    payload: sagaPayload,
}: any) {
    const { batchId, companyId } = sagaPayload;

    const errorMessage = 'Error fetching changed tasks details. Please try again later.';

    try {
        const params: any = {
            BatchId: batchId
        };

        if (!isUndefined(companyId)) {
            params.CompanyId = companyId;
        }
        
        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_BATCH_EDIT_DETAILS,
            params
        );

        const ChangedTasksData = get(res.data, 'GetBatchEditDetails');

        if (ChangedTasksData) {
            const responsePayload = {
                record: ChangedTasksData,
            };

            yield put(
                getTaskHistoryChangedTasksDataSuccessAction(responsePayload)
            );
        } else {
            yield put(
                getTaskHistoryChangedTasksDataErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryChangedTasksDataErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the task history changed tasks list.
 * @param param0
 */
function* handleGetTaskHistoryChangedTasksRequest({ payload }: any) {
    const errorMessage = `Error fetching task history's changed tasks list. Please try again later.`;

    try {
        const { filters, pageSize, currentPage } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_FOR_COMPANY, 
            {
                ...cleanFilters,
                Ascending: get(payload, 'sortAscending'),
                PageSize: pageSize,
                Skip: currentPage * TASK_HISTORY_PAGE.changedTasks.pageSize,
            }
        );

        const { TaskHistories } = get(res.data, 'GetTaskHistoryForCompany');
        const ChangedTasks = TaskHistories;

        if (ChangedTasks) {
            const responsePayload = {
                data: ChangedTasks,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangedTasks.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.changedTasks.pageSize),
                },
            };

            yield put(getTaskHistoryChangedTasksSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryChangedTasksErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryChangedTasksErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the failed task list under task history changed tasks.
 * @param param0
 */
function* handleGetTaskHistoryTasksFailedRequest({ payload }: any) {
    const errorMessage = `Error fetching tasks failed list. Please try again later.`;

    try {
        const { filters, pageSize, currentPage } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_EDIT_TASKS_FAILED_FOR_COMPANY, 
            {
                ...cleanFilters,
                PageSize: pageSize,
                Skip: currentPage * TASK_HISTORY_PAGE.failedTasks.pageSize,
            }
        );

        const { Tasks } = get(res.data, 'GetEditTasksFailedForCompany');
        const TaskHistoryTasksFailed = Tasks;

        if (TaskHistoryTasksFailed) {
            const responsePayload = {
                data: TaskHistoryTasksFailed,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(TaskHistoryTasksFailed.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.failedTasks.pageSize),
                },
            };

            yield put(getTaskHistoryTasksFailedSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryTasksFailedErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryTasksFailedErrorAction([errorMessage]));
    }
}

// Edit tasks
/**
 * Function called for updating task details.
 * @param param0
 */
function* handleBulkSaveTaskDetailsUpdateRequest({
    payload: sagaPayload,
}: any) {
    const { callback, taskObject } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
        ...taskObject,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/batch-tasks/edit',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(
                `${TaskEditAction} task${TaskEditAction > 1 ? 's have' : ' has' } been udpated successfully!`
            );
        }

        yield put(bulkSaveTaskDetailsUpdateResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        yield put(bulkSaveTaskDetailsUpdateResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleOrganisationBulkSaveTaskDetailsUpdateRequest({ payload: sagaPayload }: any) {
    const { callback, taskObject } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
        ...taskObject,
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/batch-tasks/edit`,
                payload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/batch-tasks/edit',
                options: {
                    body: payload,
                }
            });
        }

        const { TaskEditAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(
                `${TaskEditAction} task${TaskEditAction > 1 ? 's have' : ' has'} been udpated successfully!`
            );
        }
        yield put(bulkSaveTaskDetailsUpdateResponseAction());

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        yield put(bulkSaveTaskDetailsUpdateResponseAction());

        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for getting the editable properties for task editing.
 * @param param0
 */
function* handleGetTaskEditPropertiesRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
    };

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/task/action/batch-tasks/editproperties',
            'POST',
            payload
        );

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for getting the workflow step options for task editing.
 * @param param0
 */
function* handleGetWorkflowStepOptionsRequest({ payload: sagaPayload }: any) {
    try {
        const { workflowId, companyIds, callback } = sagaPayload;
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_WORKFLOW_STEPS_FOR_COMPANY,
                variables: {
                    WorkflowId: workflowId,
                    CompanyIds: companyIds
                }
            }
        );

        const workflowSteps = get(res.data, 'GetWorkflowStepsForCompany');

        if (callback) {
            callback(workflowSteps);
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the actioned ticket details.
 * @param param0
 */
function* handleGetActionedTicketDetailsRequest({ payload: sagaPayload }: any) {
    const { batchId, companyId } = sagaPayload;

    const errorMessage = `Error fetching actioned ticket details. Please try again later.`;

    try {
        const params: any = {
            BatchId: batchId
        };

        if (!isUndefined(companyId)) {
            params.CompanyId = companyId;
        }            

        const apiUrl: string | undefined = yield select(getOrganisationActionUrl);

        const response: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_ACTIONED_TICKET_DETAILS,
            params
        );

        const ActionedTicketData = get(
            response.data,
            'GetTaskHistoryActionedTicketDetails'
        );

        if (ActionedTicketData) {
            const responsePayload = {
                record: ActionedTicketData,
            };

            yield put(getTaskHistoryDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the actioned paymentplan details.
 * @param param0
 */
function* handleGetActionedPaymentPlanDetailsRequest({
    payload: sagaPayload,
}: any) {
    const { batchId, companyId } = sagaPayload;
    const params: any = {
        BatchId: batchId
    }

    if (!isUndefined(companyId)) {
        params.CompanyId = companyId;
    }
    const errorMessage = `Error fetching actioned payment plan details. Please try again later.`;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_ACTIONED_PAYMENT_PLAN_DETAILS,
            params
        );

        const ActionedPaymentPlanData = get(
            res.data,
            'GetTaskHistoryActionedPaymentPlanDetails'
        );

        if (ActionedPaymentPlanData) {
            const responsePayload = {
                record: ActionedPaymentPlanData,
            };

            yield put(getTaskHistoryDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the actioned payment allocation/verification details.
 * @param param0
 */
function* handleGetActionedPaymentDetailsRequest({
    payload: sagaPayload,
}: any) {
    const { batchId, companyId } = sagaPayload;

    const errorMessage = `Error fetching actioned payment details. Please try again later.`;
    const params: any = {
        BatchId: batchId
    };

    if (!isUndefined(companyId)) {
        params.CompanyId = companyId;
    }

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_ACTIONED_PAYMENT_DETAILS,
            params
        );

        const ActionedPaymentData = get(
            res.data,
            'GetTaskHistoryActionedPaymentDetails'
        );
        
        if (ActionedPaymentData) {
            const responsePayload = {
                record: ActionedPaymentData,
            };

            yield put(getTaskHistoryDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the actioned PaymentBehaviourInsight details.
 * @param param0
 */
function* handleGetActionedPaymentBehaviourInsightDetailsRequest({
    payload: sagaPayload,
}: any) {
    const { batchId, companyId } = sagaPayload;
    const params: any = {
        BatchId: batchId
    };

    if (!isUndefined(companyId)) {
        params.CompanyId = companyId;
    }

    const errorMessage = `Error fetching actioned payment behaviour insight details. Please try again later.`;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_ACTIONED_PAYMENT_BEHAVIOUR_INSIGHT_DETAILS,
            params
        );
                
        const ActionedPaymentBehaviourInsightsData = get(
            res.data,
            'GetTaskHistoryActionedPaymentBehaviourInsights'
        );
        
        if (ActionedPaymentBehaviourInsightsData) {
            const responsePayload = {
                record: ActionedPaymentBehaviourInsightsData,
            };

            yield put(getTaskHistoryDataSuccessAction(responsePayload));
        } else {
            yield put(getTaskHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

// Collections
/**
 * Function for getting the collection escalation details.
 * @param param0
 */
function* handleGetTaskHistoryCollectionsDataRequest({ payload }: any) {
    const errorMessage = 'Error fetching collection escalation details. Please try again later.';

    try {
        const { batchId, companyId } = payload;
        const params: any = {
            BatchId: batchId
        };

        if (!isUndefined(companyId)) {
            params.CompanyId = companyId;
        }

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_BATCH_COLLECTION_DETAILS, 
            params
        );

        const CollectionItemsData = get(res.data, 'GetBatchCollectionDetails');

        if (CollectionItemsData) {
            const responsePayload = {
                record: CollectionItemsData,
            };

            yield put(
                getTaskHistoryCollectionsDataSuccessAction(responsePayload)
            );
        } else {
            yield put(getTaskHistoryCollectionsDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryCollectionsDataErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the collection tasks actioned tasks items.
 * @param param0
 */
function* handleGetTaskHistoryCollectionTasksRequest({ payload }: any) {
    const errorMessage = `Error fetching task history's collection tasks actioned list. Please try again later.`;

    try {
        const { filters, pageSize, currentPage } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(filters);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_FOR_COMPANY, 
            {
                ...cleanFilters,
                Ascending: get(payload, 'sortAscending'),
                PageSize: pageSize,
                Skip: currentPage * TASK_HISTORY_PAGE.collections.pageSize,
            }
        );

        const { TaskHistories } = get(res.data, 'GetTaskHistoryForCompany');
        const ChangedTasks = TaskHistories;

        if (ChangedTasks) {
            const responsePayload = {
                data: ChangedTasks,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangedTasks.length < pageSize) &&
                        !(pageSize < TASK_HISTORY_PAGE.collections.pageSize),
                },
            };

            yield put(
                getTaskHistoryCollectionTasksSuccessAction(responsePayload)
            );
        } else {
            yield put(getTaskHistoryCollectionTasksErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryCollectionTasksErrorAction([errorMessage]));
    }
}

/**
 * Function for setting the selectedId for task history collections task.
 * @param param0
 */
function* handleSetSelectedTaskHistoryCollectionsTaskIdRequest({
    payload,
}: any) {
    const { taskId, callback } = payload;
    yield put(setSelectedTaskHistoryCollectionsTaskIdSuccessAction(taskId));

    callback();
}

/**
 * Function for getting the task history collections task details.
 * @param param0
 */
function* handleGetTaskHistoryCollectionsTaskDataRequest({ payload }: any) {
    const { taskId, isUsingCloudImportType, companyId } = payload;
    const errorMessage = 'Error fetching collection actioned task details. Please try again later.';

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_DETAILS_FOR_COMPANY, 
            {
                CompanyId: companyId,
                TaskId: taskId,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            }
        );

        const Task = get(res.data, 'GetTaskDetailsForCompany');

        if (Task) {
            const responsePayload = {
                record: Task,
            };

            yield put(
                getTaskHistoryCollectionsTaskDataSuccessAction(responsePayload)
            );
        } else {
            yield put(
                getTaskHistoryCollectionsTaskDataErrorAction([errorMessage])
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getTaskHistoryCollectionsTaskDataErrorAction([errorMessage]));
    }
}

/**
 * Function for escalating the selected task items (in Active tasks page) to collections.
 * @param param0
 */
function* handleEscalateToCollections({ payload: sagaPayload }: any) {
    const { usingCustomerWorkflow, callback } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: {
            ...commonTaskFilter
        },
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/collection/escalate',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: any = [];

        if (TaskEditAction) {
            const itemText = usingCustomerWorkflow ? 'Customer' : 'Invoice';

            successMessages.push(
                `${itemText}(s) escalated to collections successfully!`
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleOrganisationEscalateToCollections({ payload: sagaPayload }: any) {
    const { usingCustomerWorkflow, callback } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: {
            ...commonTaskFilter
        },
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );
    
    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');
            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/collection/escalate`,
                payload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/collection/escalate',
                options: {
                    body: payload,
                }
            });
        }
        
        const { TaskEditAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskEditAction) {
            const itemText = usingCustomerWorkflow ? 'Customer' : 'Invoice';
            successMessages.push(
                `${itemText}(s) escalated to collections successfully!`
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleProcessTaskCompactViewReduxSetting({
    payload: userPreferences,
}: any) {
    const isCompactView = getIsCompactViewForPage(
        userPreferences,
        compactViewPages.ACTIVE_TASKS
    );

    const compactViewBool = !isUndefined(isCompactView)
        ? isCompactView
        : initialState.activeTasks.compactView;

    yield put(getTableCompactViewSuccessAction(compactViewBool));
}

/**
 * Function for getting the task table compact view setting.
 * @param param0
 */
function* handleGetTableCompactViewRequest({ payload: refetchUserPrefs }: any) {
    try {
        if (refetchUserPrefs) {
            yield put(
                getUserPreferencesRequestAction((res: DynamicObject) => {
                    const preferences = get(res, 'data', []);
                    put(processTaskCompactViewReduxSettingAction(preferences));
                })
            );
        } else {
            const userPreference: UserPreference | {} = yield select(
                (state: ApplicationState) => state.users.userPreference
            );

            yield put(
                processTaskCompactViewReduxSettingAction(
                    get(userPreference, 'Preferences', [])
                )
            );
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
}

/**
 * Function that clears the task status Ids if current compact view and new compact view statuses are not equal.
 * @param param0
 */
function* handleGetTableCompactViewSuccess() {
    yield put(setTaskExpandStatusAction());
}

/**
 * Function for setting the task table compact view status.
 * @param param0
 */
function* handleSetTableCompactViewRequest({ payload: sagaPayload }: any) {
    const { isCompact, callback } = sagaPayload;

    try {
        const payload = {
            PageName: compactViewPages.ACTIVE_TASKS,
            IsCompactView: isCompact,
        };

        yield call(post, {
            apiName: API_NAME,
            path: '/pageView/userpreference',
            options: {
                body: payload
            }
        });

        yield put(setTableCompactViewSuccessAction(true)); // boolean for indicator if compact view status should be refetched or not
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        yield put(setTableCompactViewSuccessAction());

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for adding comment to principal element of tasks (from task view)
 * @param param0
 */
function* handleTaskAddCommentRequest({ payload: sagaPayload }: any) {
    const {
        taskIds,
        excludeTasks,
        comment,
        callback,
    } = sagaPayload;

    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
        Comment: comment,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/conversation/save/taskcomment',
            options: {
                body: payload
            }
        });

        if (callback) {
            let RefetchList = true;

            if (taskIds.length === 1 && excludeTasks === false) {
                let cusTasksToUpdate: DynamicObject[] = [];
                RefetchList = false;

                const currentUser: DynamicObject = yield select(getCurrentUser);
                const taskList: DynamicObject[] = yield select(getTasksData);
                const usingCustomerWorkflow: boolean = yield select(
                    getUsingCustomerWorflow
                );

                const tasksUpdated: DynamicObject[] = map(
                    taskIds,
                    (uId: string) => {
                        const taskRecord = find(taskList, ['Id', uId]);
                        let returnData: any = undefined;

                        const convLine = {
                            Content: comment,
                            CreatedDateTime: formatDateToDateObjectUTC(
                                dayjs(),
                                undefined,
                                true
                            ),
                            User: {
                                GivenName: get(currentUser, GivenNameAttribute),
                                FamilyName: get(
                                    currentUser,
                                    FamilyNameAttribute
                                ),
                            },
                        };

                        if (taskRecord && !isEmpty(taskRecord)) {
                            returnData = { ...taskRecord };

                            if (
                                taskRecord.Type === taskHistoryBatchTypes.Ticket
                            ) {
                                returnData.Ticket = {
                                    ...returnData.Ticket,
                                    ConversationLine: convLine,
                                };
                            } else if (
                                taskRecord.Type ===
                                taskHistoryBatchTypes.PaymentSchedule
                            ) {
                                const tasksWithPaymentPlan = lFilter(taskList, [
                                    'PaymentPlan.Id',
                                    returnData.PaymentPlan.Id,
                                ]);

                                if (!isEmpty(tasksWithPaymentPlan)) {
                                    cusTasksToUpdate = map(
                                        tasksWithPaymentPlan,
                                        (tr: DynamicObject) => {
                                            return {
                                                ...tr,
                                                PaymentPlan: {
                                                    ...tr.PaymentPlan,
                                                    ConversationLine: convLine,
                                                },
                                            };
                                        }
                                    );
                                }
                            } else if (
                                taskRecord.Type ===
                                taskHistoryBatchTypes.RemittanceAdvice
                            ) {
                                returnData.RemittanceAdvice = {
                                    ...returnData.RemittanceAdvice,
                                    ConversationLine: convLine
                                };
                            } else if (
                                taskRecord.Type === taskHistoryBatchTypes.NewInvoiceEmail
                            ) {
                                returnData.Invoice = {
                                    ...returnData.Invoice,
                                    ConversationLine: convLine,
                                };
                            } else if (usingCustomerWorkflow) {
                                const tasksWithCustomer = lFilter(taskList, [
                                    'Customer.CustomerCode',
                                    returnData.Customer.CustomerCode,
                                ]);

                                if (!isEmpty(tasksWithCustomer)) {
                                    cusTasksToUpdate = map(
                                        tasksWithCustomer,
                                        (tr: DynamicObject) => {
                                            return {
                                                ...tr,
                                                Customer: {
                                                    ...tr.Customer,
                                                    ConversationLine: convLine,
                                                },
                                            };
                                        }
                                    );
                                }
                            } else {
                                returnData.Invoice = {
                                    ...returnData.Invoice,
                                    ConversationLine: convLine,
                                };
                            }
                        }

                        return returnData;
                    }
                );

                const allTasksUpdated = [...tasksUpdated, ...cusTasksToUpdate];

                const pageData: PageData = yield select(getTasksPageData);

                yield put(
                    getTasksSuccessAction({
                        data: allTasksUpdated as Task[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that ccalls the action for getting the new compact view status to sync our redux value.
 * @param param0
 */
function* handleSetTableCompactViewSuccess({
    payload: refetchCompactViewState,
}: any) {

    if (refetchCompactViewState) {
        yield put(getTableCompactViewRequestAction(refetchCompactViewState));
    }

    return;
}

/**
 * Function called for getting the editable properties for task editing.
 * @param param0
 */
function* handleActionPaymentVerificationTaskRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/paymentverification',
            options: {
                body: sagaPayload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    
    } catch (err) {
        if (callback) {
            let returnData: any;

            // Check for timeout or network-related errors
            if (!err.response || err.message.includes('timeout') || err.message.includes('Network Error')) {
                returnData.Messages = ['Request timed out or a network issue occurred. Please try again.'];
                returnData.IsTimeout = true;
            } else {
                if (err instanceof Error && 'response' in err && (err as any).response.data) {
                    returnData = (err as any).response.data;
                } else if (err instanceof Error) {
                    returnData.Messages = [err.message];
                } else {
                    returnData.Messages = ['An unknown error occurred.']
                }
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleActionOrganisationPaymentVerificationTaskRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');
            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/paymentverification`,
                sagaPayload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/paymentverification',
                options: {
                    body: sagaPayload,
                }
            });
        }

        const { TaskPaymentAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskPaymentAction) {
            successMessages.push(
                'Payment verified successfully!'
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            // Check for timeout or network-related errors
            if (!err.response || err.message.includes('timeout') || err.message.includes('Network Error')) {
                returnData.Messages = ['Request timed out or a network issue occurred. Please try again.'];
                returnData.IsTimeout = true;
            } else {
                if (err instanceof Error && 'response' in err && (err as any).response.data) {
                    returnData = (err as any).response.data;
                } else if (err instanceof Error) {
                    returnData.Messages = [err.message];
                } else {
                    returnData.Messages = ['An unknown error occurred.']
                }
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleActionPaymentRALinkTaskRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/paymentallocation/remittanceadvice',
            options: {
                body: sagaPayload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskPaymentAction } = res;
        const successMessages: any = [];

        if (TaskPaymentAction) {
            successMessages.push(
                'Payment linked successfully!'
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

function* handleActionOrganisationPaymentRALinkTaskRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');
            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/paymentallocation/remittanceadvice`,
                sagaPayload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/paymentallocation/remittanceadvice',
                options: {
                    body: sagaPayload,
                }
            });
        }

        const { TaskPaymentAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskPaymentAction) {
            successMessages.push(
                'Payment linked successfully!'
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }    
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

function* handleActionRequestRATaskRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/paymentallocation/remittanceadvicerequest',
            options: {
                body: sagaPayload,
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskPaymentAction } = res;
        const successMessages: any = [];

        if (TaskPaymentAction) {
            successMessages.push(
                'Remittance advice request sent successfully!'
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }    
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

function* handleActionOrganisationRequestRATaskRequest({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');
            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/paymentallocation/remittanceadvicerequest`,
                sagaPayload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/paymentallocation/remittanceadvicerequest',
                options: {
                    body: sagaPayload,
                }
            });
        }

        const { TaskPaymentAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskPaymentAction) {
            successMessages.push(
                'Remittance advice request sent successfully!'
            );
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }    
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }
    }
}

/**
 * Function called when actioning manually allocate payment task.
 * @param param0
 */
function* handleActionManualAllocatePaymentRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/manual-allocate-payment',
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: { Messages: string[]; IsSuccess: boolean; StatusCode?: number; IsTimeout?: boolean } = {
                Messages: ['An unknown error occurred.'],
                IsSuccess: false,
            };
            // Check if there is a valid HTTP response (err.response exists)
            if (get(err, 'response')) {
                returnData = get(err, 'response.data', returnData);
                returnData.StatusCode = get(err, 'response.status');
            } 
            
            // Check for timeout or network-related errors
            if (!err.response || err.message.includes('timeout') || err.message.includes('Network Error')) {
                returnData.Messages = ['Request timed out or a network issue occurred. Please try again.'];
                returnData.IsTimeout = true;
            }
    
            callback(returnData);
        }
    
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleActionOrganisationManualAllocatePaymentRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/manual-allocate-payment`,
                payload, { Authorization: accessToken }
            );
        } else {
            yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/manual-allocate-payment',
                options: {
                    body: payload,
                }
            });
        }

        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called when actioning manually allocate payment with overpayment task.
 * @param param0
 */
function* handleActionManualAllocatePaymentWithOverpaymentRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/over-payment-credit', // for update
            options: {
                body: payload,
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: DynamicObject;

            if (typeof err === 'object' && err !== null && 'response' in err) {
                const errorResponse = err as { response: { data: any } };

                returnData = get(errorResponse.response, 'data')
                    ? errorResponse.response.data
                    : { Messages: ['An error occurred'] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the `Action` options on resolving ticket panel.
 * @param param0
 */
function* handleGetTriggersForWorkflowRequest({ payload: sagaPayload }: any) {
    const { workflowId, stateName, companyId, callback } = sagaPayload;
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TRIGGERS_FOR_WORKFLOW_STATE, 
                variables: {
                    WorkflowId: workflowId,
                    StateName: stateName,
                    CompanyId: companyId
                }
            }
        );

        const { WorkflowTransitions: triggerOptions } = get(
            res,
            'data.GetTriggersForWorkflowState'
        );

        if (callback) {
            callback(triggerOptions);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the `Action` options on resolving ticket panel.
 * @param param0
 */
function* handleGetActionTaskOptionsRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TRIGGERS_FOR_WORKFLOW_STATES,
                variables: {
                    CompanyId: payload.CompanyId,
                    WorkflowStates: payload.WorkflowStates
                }
            }
        );

        const { WorkflowTransitions: triggerOptions } = get(
            res,
            'data.GetTriggersForWorkflowStates'
        );

        if (callback) callback(triggerOptions);
    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the `RemittanceAdvices` Lists on TaskActionPaymentLinkRemittanceContent panel.
 * @param param
 */
function* handleGetRemittanceAdvicesRequest({ payload: sagaPayload }: any) {
    const { 
        CompanyId, 
        CustomerId,
        RemittanceAdviceState, 
        PaidDateMin, 
        PaidDateMax, 
        PaidAmountMin, 
        PaidAmountMax, 
        SortField,
        Ascending,
        AmountType,
        Skip,
        PageSize,
        callback
    } = sagaPayload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REMITTANCE_ADVICES_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    CustomerId: CustomerId,
                    RemittanceAdviceState: RemittanceAdviceState,
                    AmountType: AmountType,
                    PaidDateMin: PaidDateMin,
                    PaidDateMax: PaidDateMax,
                    PaidAmountMin: PaidAmountMin,
                    PaidAmountMax: PaidAmountMax,
                    SortField: SortField,
                    Ascending: Ascending,
                    Skip: Skip,
                    PageSize: PageSize,
                    callback
                }
            }
        );

        const { RemittanceAdvices } = get(res.data, 'GetRemittanceAdvicesForCompany');

        if (callback) {
            callback(RemittanceAdvices);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}
/**
 * Function called when actioning payment behaviour insight tasks.
 * @param param0
 */
function* handleTaskPaymentBehaviorInsightRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/payment-behaviour-insight',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskPaymentBehaviorInsightAction } = res;
        const successMessages: any = [];

        if (TaskPaymentBehaviorInsightAction) {
            successMessages.push(`Payment behavior insight task has been saved successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleTaskOrganisationPaymentBehaviorInsightRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;

    const payload = {
        ...fields,
    };

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        let res: DynamicObject = {};

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            res = yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/payment-behaviour-insight`,
                payload, { Authorization: accessToken }
            );
        } else {
            res = yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/payment-behaviour-insight',
                options: {
                    body: payload,
                }
            });
        }

        const { TaskPaymentBehaviorInsightAction } = yield res.body.json();
        const successMessages: any = [];

        if (TaskPaymentBehaviorInsightAction) {
            successMessages.push(`Payment behavior insight task has been saved successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/** 
 * Function called for adding task viewings.
 * @param param0
 */
function* handleAddTaskViewingsRequest({
    payload: sagaPayload,
}: any) {
    const { callback } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });
    
    const payload = {
        TaskManagementFilter: { ...commonTaskFilter }
    };

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/task/viewing',
            'POST',
            payload
        );

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function called for deleting task viewings.
 * @param param0
 */
function* handleDeleteTaskViewingsRequest({
    payload: sagaPayload,
}: any) {
    const { callback } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter }
    };

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/task/viewing',
            'DELETE',
            payload
        );

        if (callback) {
            const response = {
                IsSuccess: true
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the tasks viewer detail for user.
 * @param param0
 */
function* handleGetTaskViewerDetailForUserRequest({ payload: sagaPayload }: any) {
    let { refetchCount, ...payload } = sagaPayload;
    refetchCount = refetchCount || 0;
    const errorMessage = 'Error fetching task viewer detail. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: false });

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_VIEWER_DETAIL_FOR_USER,
                variables: {
                    ...commonTaskFilter
                }
            }
        );

        const response = get(res.data, 'GetTaskViewerDetailForUser');
        const User = get(response, 'User');
        refetchCount = 0;

        yield put(getTaskViewerDetailForUserSuccessAction(User));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTaskViewerDetailForUserRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getTaskViewerDetailForUserErrorAction([errorMessage]));
        }
    }
}

function* handleGetTaskViewerDetailForUserOrganisationRequest({ payload: sagaPayload }: any) {
    const { ...payload } = sagaPayload;
    let { refetchCount } = sagaPayload;
    refetchCount = refetchCount || 0;
    const errorMessage = 'Error fetching task viewer detail. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: false });

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_VIEWER_DETAIL_FOR_USER_ORGANISATION,
            {
                ...commonTaskFilter
            }
        );

        const response = get(res.data, 'GetTaskViewerDetailForUserOrganisation');
        const User = get(response, 'User');
        refetchCount = 0;

        yield put(getTaskViewerDetailForUserSuccessAction(User));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getTaskViewerDetailForUserOrganisationRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getTaskViewerDetailForUserErrorAction([errorMessage]));
        }
    }
}

/**
 * Function for getting the tasks custom fields for a company.
 * @param param0
 */
function* handleGetTaskCustomerCustomFieldsForCompanyRequest({ payload }: any) {
    /*const errorMessage = '';*/
    const { 
        CompanyId, 
        FieldName,
        callback
    } = payload;

    try {
        //const errorMessage =
        //'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_CUSTOMER_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetTaskCustomerCustomFieldsForCompany');

        if (callback) {
            CustomFieldValues.IsSuccess = true;

            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleTaskActionPhoneCallTaskRequest({ payload: sagaPayload }: any) {
    const { callback, Comment, PromiseToPayDate, ...payload } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: true });

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/task/action/phone-call',
            options: {
                body: {
                    TaskManagementFilter: { ...commonTaskFilter },
                    Comment,
                    PromiseToPayDate,
                    Trigger: 'Action Phone Call'
                }
            }
        });

        if (callback) {
            const response = { IsSuccess: true };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleTaskActionOrganisationPhoneCallTaskRequest({ payload: sagaPayload }: any) {
    const { callback, Comment, PromiseToPayDate, ...payload } = sagaPayload;
    const commonTaskFilter = preparePayloadForTaskFilter({ payload, canExcludeTasks: true, parseCustomFields: true });

    const apiUrl: string | undefined = yield select(
        getOrganisationActionUrl
    );

    try {
        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = get(currentSession, 'tokens.accessToken');

            yield call(
                customPostData,
                `${apiUrl}/task/action/organisation/phone-call`,
                {
                    TaskManagementFilter: { ...commonTaskFilter },
                    Comment,
                    PromiseToPayDate,
                    Trigger: 'Action Phone Call'
                }, 
                { Authorization: accessToken }
            );
        } else {
            yield call(post, {
                apiName: API_NAME,
                path: '/task/action/organisation/phone-call',
                options: {
                    body: {
                        TaskManagementFilter: { ...commonTaskFilter },
                        Comment,
                        PromiseToPayDate,
                        Trigger: 'Action Phone Call'
                    },
                }
            });
        }

        if (callback) {
            const response = { IsSuccess: true };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the tasks custom fields for a company.
 * @param param0
 */
function* handleGetTaskInvoiceCustomFieldsForCompanyRequest({ payload }: any) {
    /*const errorMessage = '';*/
    const { 
        CompanyId, 
        FieldName,
        callback
    } = payload;

    try {
        //const errorMessage =
        //'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_INVOICE_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetTaskInvoiceCustomFieldsForCompany');

        if (callback) {
            CustomFieldValues.IsSuccess = true;

            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleGetActionedPhoneCallDataRequest({ payload }: any) {
    const errorMessage = `Error fetching actioned phone call task details. Please try again later.`;
    const { BatchId, CompanyId, IsUsingCloudImportType, UsingCustomerWorkflow } = payload;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_ACTIONED_PHONE_CALL_TASK_DETAILS,
            {
                BatchId,
                CompanyId,
                IsUsingCloudImportType,
                UsingInvoiceWorkflow: !UsingCustomerWorkflow
            }
        );

        const record = get(res.data, 'GetTaskHistoryActionedPhoneCallDetails');

        if (record) {
            yield put(getTaskHistoryDataSuccessAction({ record }));
        } else {
            yield put(getTaskHistoryDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

//function TasksFetchedForOrganisationReceived(params: { pageSize: number, currentPage: number }) {
//    return function (data: TasksFetchedData) {
//        store.dispatch(tasksFetchedForOrganisationAction({ data, ...params }));
//    };
//}

function* watchTasksFetchedForOrganisationReceived() {
    yield takeLatest(TasksActionTypes.TASKS_FETCHED_FOR_ORGANISATION_RECEIVED, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const tasks: Task[] = data.OnTasksFetchedForOrganisation.Tasks;

        const responsePayload = {
            data: tasks,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(tasks.length < pageSize) &&
                    !(pageSize < TASKS_PAGE.pageSize),
            },
        };

        refetchCount = 0;

        yield put(tasksFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getTasksSuccessAction(responsePayload));
    });
}

function* handleGetTaskWorkflowsRequest({ payload }: any) {
    const errorMessage = 'Error fetching task workflows. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_WORKFLOWS_FOR_COMPANY, 
                variables: {
                    WorkflowFilters: payload
                }
            }
        );

        const Workflows = get(res.data, 'GetTaskWorkflowsForCompany');

        if (Workflows) {
            refetchCount = 0;
            yield put(getTaskWorkflowsSuccessAction(Workflows));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getTaskWorkflowsRequestAction(payload));
        } else {
            yield put(getTaskWorkflowsErrorAction([errorMessage]));
        }
    }
}

/**
 * Function called when actioning a payment plan task.
 * @param param0
 */
function* handleActionCancelPaymentPlanRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;
    const { Trigger, Comment } = fields
    const commonTaskFilter = preparePayloadForTaskFilter({ payload: sagaPayload, canExcludeTasks: true, parseCustomFields: true });

    const payload = {
        TaskManagementFilter: { ...commonTaskFilter },
        Trigger: Trigger,
        Comment: Comment
    };

    try {
        const res: DynamicObject = yield call(post, {
            apiName: API_NAME,
            path: '/task/action/cancelpaymentplan',
            options: {
                body: {
                    ...payload
                },
            }
        });
        const { TaskEditAction } = yield res.body.json();
        const successMessages: string[] = [];

        if (TaskEditAction) {
            successMessages.push(`Task has been actioned successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.data) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function for getting the actioned cancel paymentplan details.
 * @param param0
 */
function* handleGetActionedCancelPaymentPlanDetailsRequest({ payload }: any) {
    const errorMessage = `Error fetching actioned cancel payment plan details. Please try again later.`;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            isCancelledPaymentPlanList
        } = payload;

        // To call async functions, use redux-saga's `call()`.
        const sortFields = populateSortFieldsParamSortParams(sortBy);
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'task'
        );

        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_TASK_HISTORY_ACTIONED_CANCEL_PAYMENT_PLAN_DETAILS,
            {
                ...cleanFilters,
                ...sortFields,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * TASK_HISTORY_PAGE.cancelledPaymentPlan.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
            }
        );
        const { TaskHistoryActionedCancelPaymentPlanList } = get(
            res.data,
            'GetTaskHistoryActionedCancelPaymentPlanDetails'
        );

        const cancelledPaymentPlans = TaskHistoryActionedCancelPaymentPlanList;

        if (cancelledPaymentPlans) {
            if (isCancelledPaymentPlanList) {
                const responsePayload = {
                    data: TaskHistoryActionedCancelPaymentPlanList,
                    pageData: {
                        pageSize: pageSize,
                        currentPage: currentPage,
                        hasNextPage:
                            !(cancelledPaymentPlans.length < pageSize) &&
                            !(pageSize < TASK_HISTORY_PAGE.cancelledPaymentPlan.pageSize),
                    }
                }
                
                yield put(getTaskHistoryCancelledPaymentPlanTasksSuccessAction(responsePayload));
            } else {
                const responsePayload = {
                    record: !isEmpty(TaskHistoryActionedCancelPaymentPlanList) ? TaskHistoryActionedCancelPaymentPlanList[0] : {},
                };
                
                yield put(getTaskHistoryDataSuccessAction(responsePayload));
            }
        } else {
            if (isCancelledPaymentPlanList) {
                yield put(getTaskHistoryCancelledPaymentPlanTasksErrorAction([errorMessage]));
            } else {
                yield put(getTaskHistoryDataErrorAction([errorMessage]));
            }
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        yield put(getTaskHistoryDataErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the tasks custom fields for an organisation.
 * @param param0
 */
function* handleGetTaskCustomerCustomFieldsForOrganisationRequest({ payload }: any) {
    /*const errorMessage = '';*/
    const { 
            CompanyIds, 
            FieldName,
            callback
        } = payload;
    try {
        //const errorMessage =
        //'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_CUSTOMER_CUSTOM_FIELDS_FOR_ORGANISATION,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetTaskCustomerCustomFieldsForOrganisation');

        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }

    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the tasks custom fields for a company.
 * @param param0
 */
function* handleGetTaskInvoiceCustomFieldsForOrganisationRequest({ payload }: any) {
    /*const errorMessage = '';*/
    const { 
            CompanyIds, 
            FieldName,
            callback
        } = payload;
    try {
        //const errorMessage =
        //'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_TASK_INVOICE_CUSTOM_FIELDS_FOR_ORGANISATION,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetTaskInvoiceCustomFieldsForOrganisation');

        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }

    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetTasksRequest() {
    yield takeLatest(
        getTasksRequestAction.type,
        handleGetTasksRequest
    );
}

function* watchGetTaskWorkflowsRequest() {
    yield takeLatest(
        getTaskWorkflowsRequestAction.type,
        handleGetTaskWorkflowsRequest
    );
}

function* watchGetTasksForOrganisationRequest() {
    yield takeLatest(
        getTasksForOrganisationRequestAction.type,
        handleGetTasksForOrganisationRequest
    );
}

function* watchCheckTasksReadyStateRequest() {
    yield takeLatest(
        checkTasksReadyStateAction.type,
        handleCheckTasksReadyStateRequest
    );
}

function* watchCheckTasksReadyStateForOrganisationRequest() {
    yield takeLatest(
        checkTasksReadyStateActionForOrganisation.type,
        handleCheckTasksReadyStateForOrganisationRequest
    );
}

function* watchNotifyCustomersRequest() {
    yield takeLatest(
        notifyCustomersRequestAction.type,
        handleNotifyCustomersRequest
    );
}

function* watchNotifyOrganisationCustomersRequest() {
    yield takeLatest(
        notifyOrganisationCustomersRequestAction.type,
        handleNotifyOrganisationCustomersRequest
    );
}

function* watchGetTasksActionFilterOptionsRequest() {
    yield takeLatest(
        getTasksActionFilterOptionsRequestAction.type,
        handleGetTasksActionFilterOptionsRequest
    );
}

function* watchGetTaskDataRequest() {
    yield takeLatest(
        getTaskDataRequestAction.type,
        handleGetTaskDataRequest
    );
}

function* watchGetTaskDataForOrganisationRequest() {
    yield takeLatest(
        getTaskDataForOrganisationRequestAction.type,
        handleGetTaskDataForOrganisationRequest
    );
}

function* watchSetSelectedTaskIdRequest() {
    yield takeLatest(
        setSelectedTaskIdRequestAction.type,
        handleSetSelectedTaskIdRequest
    );
}

function* watchGetTaskTicketInvoicesRequest() {
    yield takeLatest(
        getTaskTicketInvoicesRequestAction.type,
        handleGetTaskTicketInvoicesRequest
    );
}

function* watchGetTaskPaymentPlanScheduleRequest() {
    yield takeLatest(
        getTaskPaymentPlanScheduleRequestAction.type,
        handleGetTaskPaymentPlanScheduleRequest
    );
}

/**
 * Task History watchers
 */
function* watchGetTaskHistoryRequest() {
    yield takeLatest(
        getTaskHistoryRequestAction.type,
        handleGetTaskHistoryRequest
    );
}

function* watchGetTaskHistoryDataRequest() {
    yield takeLatest(
        getTaskHistoryDataRequestAction.type,
        handleGetTaskHistoryDataRequest
    );
}

function* watchSetSelectedTaskHistoryIdRequest() {
    yield takeLatest(
        setSelectedTaskHistoryIdRequestAction.type,
        handleSetSelectedTaskHistoryIdRequest
    );
}

// Task History notifications
function* watchSetSelectedTaskHistoryNotificationIdRequest() {
    yield takeLatest(
        setSelectedTaskHistoryNotificationIdRequestAction.type,
        handleSetSelectedTaskHistoryNotificationIdRequest
    );
}

function* watchGetTaskHistoryNotificationsRequest() {
    yield takeLatest(
        getTaskHistoryNotificationsRequestAction.type,
        handleGetTaskHistoryNotificationsRequest
    );
}

function* watchGetTaskHistoryDeliveryReportRequest() {
    yield takeLatest(
        getTaskHistoryDeliveryReportRequestAction.type,
        handleGetTaskHistoryDeliveryReportRequest
    );
}

function* watchGetTaskHistoryNotificationDataRequest() {
    yield takeLatest(
        getTaskHistoryNotificationDataRequestAction.type,
        handleGetTaskHistoryNotificationDataRequest
    );
}

function* watchGetOrganisationTaskHistoryNotificationDataRequest() {
    yield takeLatest(
        getOrganisationTaskHistoryNotificationDataRequestAction.type,
        handleGetOrganisationTaskHistoryNotificationDataRequest
    );
}

// Notification Invoices
function* watchGetOrganisationTaskHistoryNotificationInvoicesRequest() {
    yield takeLatest(
        getOrganisationTaskHistoryNotificationInvoicesRequestAction.type,
        handleGetOrganisationTaskHistoryNotificationInvoicesRequest
    );
}

function* watchGetTaskHistoryNotificationInvoicesRequest() {
    yield takeLatest(
        getTaskHistoryNotificationInvoicesRequestAction.type,
        handleGetTaskHistoryNotificationInvoicesRequest
    );
}

// Notification Tasks
function* watchGetTaskHistoryNotificationTasksRequest() {
    yield takeLatest(
        getTaskHistoryNotificationTasksRequestAction.type,
        handleGetTaskHistoryNotificationTasksRequest
    );
}

// Notification job status
function* watchGetTaskHistoryQueuedTasksCountRequest() {
    yield takeLatest(
        getTaskHistoryQueuedTasksCountRequestAction.type,
        handleGetTaskHistoryQueuedTasksCountRequest
    );
}

function* watchGetTaskHistoryBatchStatusRequest() {
    yield takeLatest(
        getTaskHistoryBatchStatusRequestAction.type,
        handleGetTaskHistoryBatchStatusRequest
    );
}

function* watchDownloadMergedPdfRequest() {
    yield takeLatest(
        downloadMergedPdfRequestAction.type,
        handleDownloadMergedPdfRequest
    );
}

function* watchDownloadMergedPdfStatusRequest() {
    yield takeLatest(
        downloadMergedPdfStatusRequestAction.type,
        handleDownloadMergedPdfStatusRequest
    );
}

// Task history actioned tasks data
function* watchGetTaskHistoryActionedTasksDataRequest() {
    yield takeLatest(
        getTaskHistoryActionedTasksDataRequestAction.type,
        handleGetTaskHistoryActionedTasksDataRequest
    );
}

// Task history changed tasks
function* watchGetTaskHistoryActionedTasksRequest() {
    yield takeLatest(
        getTaskHistoryActionedTasksRequestAction.type,
        handleGetTaskHistoryChangedTasksRequest
    );
}


// Task history changed tasks data
function* watchGetTaskHistoryChangedTasksDataRequest() {
    yield takeLatest(
        getTaskHistoryChangedTasksDataRequestAction.type,
        handleGetTaskHistoryChangedTasksDataRequest
    );
}

// Task history changed tasks
function* watchGetTaskHistoryChangedTasksRequest() {
    yield takeLatest(
        getTaskHistoryChangedTasksRequestAction.type,
        handleGetTaskHistoryChangedTasksRequest
    );
}

// Task history tasks failed
function* watchGetTaskHistoryTasksFailedRequest() {
    yield takeLatest(
        getTaskHistoryTasksFailedRequestAction.type,
        handleGetTaskHistoryTasksFailedRequest
    );
}

// Edit tasks
function* watchBulkSaveTaskDetailsUpdateRequest() {
    yield takeLatest(
        bulkSaveTaskDetailsUpdateRequestAction.type,
        handleBulkSaveTaskDetailsUpdateRequest
    );
}

// Edit tasks
function* watchOrganisationBulkSaveTaskDetailsUpdateRequest() {
    yield takeLatest(
        organisationBulkSaveTaskDetailsUpdateRequestAction.type,
        handleOrganisationBulkSaveTaskDetailsUpdateRequest
    );
}

function* watchGetTaskEditPropertiesRequest() {
    yield takeLatest(
        getTaskEditPropertiesRequestAction.type,
        handleGetTaskEditPropertiesRequest
    );
}

function* watchGetWorkflowStepOptionsRequest() {
    yield takeLatest(
        getWorkflowStepOptionsRequestAction.type,
        handleGetWorkflowStepOptionsRequest
    );
}

function* watchGetActionedTicketDetailsRequest() {
    yield takeLatest(
        getActionedTicketDetailsRequestAction.type,
        handleGetActionedTicketDetailsRequest
    );
}

function* watchGetActionedPaymentPlanDetailsRequest() {
    yield takeLatest(
        getActionedPaymentPlanDetailsRequestAction.type,
        handleGetActionedPaymentPlanDetailsRequest
    );
}

function* watchGetActionedPaymentDetailsRequest() {
    yield takeLatest(
        getActionedPaymentDetailsRequestAction.type,
        handleGetActionedPaymentDetailsRequest
    );
}

// Collections
function* watchGetTaskHistoryCollectionsDataRequest() {
    yield takeLatest(
        getTaskHistoryCollectionsDataRequestAction.type,
        handleGetTaskHistoryCollectionsDataRequest
    );
}

function* watchGetTaskHistoryCollectionTasksRequest() {
    yield takeLatest(
        getTaskHistoryCollectionTasksRequestAction.type,
        handleGetTaskHistoryCollectionTasksRequest
    );
}

function* watchSetSelectedTaskHistoryCollectionsTaskIdRequest() {
    yield takeLatest(
        setSelectedTaskHistoryCollectionsTaskIdRequestAction.type,
        handleSetSelectedTaskHistoryCollectionsTaskIdRequest
    );
}

function* watchGetTaskHistoryCollectionsTaskDataRequest() {
    yield takeLatest(
        getTaskHistoryCollectionsTaskDataRequestAction.type,
        handleGetTaskHistoryCollectionsTaskDataRequest
    );
}

function* watchEscalateToCollections() {
    yield takeLatest(
        escalateToCollectionsAction.type,
        handleEscalateToCollections
    );
}

function* watchOrganisationEscalateToCollections() {
    yield takeLatest(
        organisationEscalateToCollectionsAction.type,
        handleOrganisationEscalateToCollections
    );
}

function* watchGetTableCompactViewRequest() {
    yield takeLatest(
        getTableCompactViewRequestAction.type,
        handleGetTableCompactViewRequest
    );
}

function* watchGetTableCompactViewSuccess() {
    yield takeLatest(
        getTableCompactViewSuccessAction.type,
        handleGetTableCompactViewSuccess
    );
}

function* watchSetTableCompactViewRequest() {
    yield takeLatest(
        setTableCompactViewRequestAction.type,
        handleSetTableCompactViewRequest
    );
}

function* watchSetTableCompactViewSuccess() {
    yield takeLatest(
        setTableCompactViewSuccessAction.type,
        handleSetTableCompactViewSuccess
    );
}

function* watchProcessTaskCompactViewReduxSetting() {
    yield takeLatest(
        processTaskCompactViewReduxSettingAction.type,
        handleProcessTaskCompactViewReduxSetting
    );
}

function* watchTaskAddCommentRequest() {
    yield takeLatest(
        taskAddCommentRequestAction.type,
        handleTaskAddCommentRequest
    );
}

function* watchActionPaymentVerificationTaskRequest() {
    yield takeLatest(
        taskActionPaymentVerificationTaskRequest.type,
        handleActionPaymentVerificationTaskRequest
    );
}

function* watchActionOrganisationPaymentVerificationTaskRequest() {
    yield takeLatest(
        taskActionOrganisationPaymentVerificationTaskRequest.type,
        handleActionOrganisationPaymentVerificationTaskRequest
    );
}

function* watchActionPaymentRALinkTaskRequest() {
    yield takeLatest(
        taskActionPaymentRALinkTaskRequest.type,
        handleActionPaymentRALinkTaskRequest
    );
}

function* watchActionOrganisationPaymentRALinkTaskRequest() {
    yield takeLatest(
        taskActionOrganisationPaymentRALinkTaskRequest.type,
        handleActionOrganisationPaymentRALinkTaskRequest
    );
}

function* watchActionRequestRATaskRequest() {
    yield takeLatest(
        taskActionRequestRATaskRequest.type,
        handleActionRequestRATaskRequest
    );
}

function* watchActionOrganisationRequestRATaskRequest() {
    yield takeLatest(
        taskActionOrganisationRequestRATaskRequest.type,
        handleActionOrganisationRequestRATaskRequest
    );
}

function* watchGetTriggersForWorkflowRequest() {
    yield takeLatest(
        getTriggersForWorkflowRequestAction.type,
        handleGetTriggersForWorkflowRequest
    );
}

function* watchGetActionTaskOptionsRequest() {
    yield takeLatest(
        getActionTaskOptionsRequestAction.type,
        handleGetActionTaskOptionsRequest
    );
}

function* watchActionManualAllocatePayment() {
    yield takeLatest(
        actionManualAllocatePaymentRequest.type,
        handleActionManualAllocatePaymentRequest
    );
}

function* watchOrganisationActionManualAllocatePayment() {
    yield takeLatest(
        actionOrganisationManualAllocatePaymentRequest.type,
        handleActionOrganisationManualAllocatePaymentRequest
    );
}

function* watchActionManualAllocatePaymentWithOverpayment() {
    yield takeLatest(
        TasksActionTypes.MANUALLY_ALLOCATE_PAYMENT_WITH_OVERPAYMENT_REQUEST,
        handleActionManualAllocatePaymentWithOverpaymentRequest
    );
}

function* watchGetRemittanceAdvicesRequest() {
    yield takeLatest(
        getRemittanceAdvicesRequestAction.type,
        handleGetRemittanceAdvicesRequest
    );
}

function* watchGetPaymentBehaviourInsightsDetailsRequest() {
    yield takeLatest(
        getActionedPaymentBehaviourInsightRequestAction.type,
        handleGetActionedPaymentBehaviourInsightDetailsRequest
    )
}

function* watchTaskPaymentBehaviorInsightRequest() {
    yield takeLatest(
        taskPaymentBehaviorInsightsRequestAction.type,
        handleTaskPaymentBehaviorInsightRequest
        );
}

function* watchTaskOrganisationPaymentBehaviorInsightRequest() {
    yield takeLatest(
        taskOrganisationPaymentBehaviorInsightsRequestAction.type,
        handleTaskOrganisationPaymentBehaviorInsightRequest
        );
}

function* watchAddTaskViewingsRequest() {
    yield takeLatest(
        addTaskViewingsRequestAction.type,
        handleAddTaskViewingsRequest
    );
}

function* watchDeleteTaskViewingsRequest() {
    yield takeLatest(
        deleteTaskViewingsRequestAction.type,
        handleDeleteTaskViewingsRequest
    );
}

function* watchGetTaskViewerDetailForUserRequest() {
    yield takeLatest(
        getTaskViewerDetailForUserRequestAction.type,
        handleGetTaskViewerDetailForUserRequest
    );
}

function* watchGetTaskCustomerCustomFieldsForCompanyRequest() {
    yield takeLatest(
        getTaskCustomerCustomFieldsForCompanyRequestAction.type,
        handleGetTaskCustomerCustomFieldsForCompanyRequest
    );
}

function* watchGetTaskInvoiceCustomFieldsForCompanyRequest() {
    yield takeLatest(
        getTaskInvoiceCustomFieldsForCompanyRequestAction.type,
        handleGetTaskInvoiceCustomFieldsForCompanyRequest
    );
}

function* watchGetTaskCustomerCustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getTaskCustomerCustomFieldsForOrganisationRequestAction.type,
        handleGetTaskCustomerCustomFieldsForOrganisationRequest
    );
}

function* watchGetTaskInvoiceCustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getTaskInvoiceCustomFieldsForOrganisationRequestAction.type,
        handleGetTaskInvoiceCustomFieldsForOrganisationRequest
    );
}

function* watchGetTaskViewerDetailForUserOrganisationRequest() {
    yield takeLatest(
        getTaskViewerDetailForUserOrganisationRequestAction.type,
        handleGetTaskViewerDetailForUserOrganisationRequest
    );
}

function* watchTaskActionPhoneCallTaskRequest() {
    yield takeLatest(
        taskActionPhoneCallTaskRequest.type,
        handleTaskActionPhoneCallTaskRequest
    );
}

function* watchTaskActionOrganisationPhoneCallTaskRequest() {
    yield takeLatest(
        taskActionOrganisationPhoneCallTaskRequest.type,
        handleTaskActionOrganisationPhoneCallTaskRequest
    );
}

function* watchGetActionedPhoneCallDataRequest() {
    yield takeLatest(
        getActionedPhoneCallDataRequestAction.type,
        handleGetActionedPhoneCallDataRequest
    );
}

function* watchGetOrganisationTasksHistoryRequest() {
    yield takeLatest(
        getOrganisationTasksHistoryRequestAction.type,
        handleGetOrganisationTasksHistoryRequest
    )
}

function* watchTaskActionCancelPaymentPlanRequest() {
    yield takeLatest(
        taskActionCancelPaymentPlanRequestAction.type,
        handleActionCancelPaymentPlanRequest
    )
}

function* watchGetActionedCancelPaymentPlanDetailsRequest() {
    yield takeLatest(
        getActionedCancelPaymentPlanDetailsRequestAction.type,
        handleGetActionedCancelPaymentPlanDetailsRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* tasksSaga() {
    yield all([
        fork(watchGetTasksRequest),
        fork(watchGetTasksForOrganisationRequest),
        fork(watchCheckTasksReadyStateRequest),
        fork(watchCheckTasksReadyStateForOrganisationRequest),
        fork(watchNotifyCustomersRequest),
        fork(watchNotifyOrganisationCustomersRequest),
        fork(watchGetTasksActionFilterOptionsRequest),
        fork(watchGetTaskDataRequest),
        fork(watchGetTaskDataForOrganisationRequest),
        fork(watchSetSelectedTaskIdRequest),
        fork(watchGetTaskTicketInvoicesRequest),
        fork(watchGetTaskPaymentPlanScheduleRequest),
        fork(watchGetTaskHistoryRequest),
        fork(watchGetTaskHistoryDataRequest),
        fork(watchSetSelectedTaskHistoryIdRequest),
        fork(watchSetSelectedTaskHistoryNotificationIdRequest),
        fork(watchGetTaskHistoryNotificationsRequest),
        fork(watchGetTaskHistoryDeliveryReportRequest),
        fork(watchGetTaskHistoryNotificationDataRequest),
        fork(watchGetTaskHistoryNotificationInvoicesRequest),
        fork(watchGetTaskHistoryNotificationTasksRequest),
        fork(watchGetTaskHistoryQueuedTasksCountRequest),
        fork(watchGetTaskHistoryBatchStatusRequest),
        fork(watchDownloadMergedPdfRequest),
        fork(watchDownloadMergedPdfStatusRequest),
        fork(watchGetTaskHistoryChangedTasksDataRequest),
        fork(watchGetTaskHistoryActionedTasksDataRequest),
        fork(watchGetTaskHistoryActionedTasksRequest),
        fork(watchGetTaskHistoryChangedTasksRequest),
        fork(watchGetTaskHistoryTasksFailedRequest),
        fork(watchBulkSaveTaskDetailsUpdateRequest),
        fork(watchOrganisationBulkSaveTaskDetailsUpdateRequest),
        fork(watchGetTaskEditPropertiesRequest),
        fork(watchGetWorkflowStepOptionsRequest),
        fork(watchGetActionedTicketDetailsRequest),
        fork(watchGetActionedPaymentPlanDetailsRequest),
        fork(watchGetTaskHistoryCollectionsDataRequest),
        fork(watchGetTaskHistoryCollectionTasksRequest),
        fork(watchSetSelectedTaskHistoryCollectionsTaskIdRequest),
        fork(watchGetTaskHistoryCollectionsTaskDataRequest),
        fork(watchEscalateToCollections),
        fork(watchOrganisationEscalateToCollections),
        fork(watchGetTableCompactViewRequest),
        fork(watchGetTableCompactViewSuccess),
        fork(watchSetTableCompactViewRequest),
        fork(watchSetTableCompactViewSuccess),
        fork(watchProcessTaskCompactViewReduxSetting),
        fork(watchTaskAddCommentRequest),
        fork(watchActionManualAllocatePayment),
        fork(watchOrganisationActionManualAllocatePayment),
        fork(watchActionManualAllocatePaymentWithOverpayment),
        fork(watchActionPaymentVerificationTaskRequest),
        fork(watchActionOrganisationPaymentVerificationTaskRequest),
        fork(watchActionPaymentRALinkTaskRequest),
        fork(watchGetActionedPaymentDetailsRequest),
        fork(watchGetTriggersForWorkflowRequest),
        fork(watchGetActionTaskOptionsRequest),
        fork(watchGetRemittanceAdvicesRequest),
        fork(watchGetPaymentBehaviourInsightsDetailsRequest),
        fork(watchTaskPaymentBehaviorInsightRequest),
        fork(watchTaskOrganisationPaymentBehaviorInsightRequest),
        fork(watchActionRequestRATaskRequest),
        fork(watchAddTaskViewingsRequest),
        fork(watchDeleteTaskViewingsRequest),
        fork(watchGetTaskViewerDetailForUserRequest),
        fork(watchGetTaskWorkflowsRequest),
        fork(watchGetTaskCustomerCustomFieldsForCompanyRequest),
        fork(watchGetTaskInvoiceCustomFieldsForCompanyRequest),
        fork(watchGetTaskViewerDetailForUserOrganisationRequest),
        fork(watchGetTaskCustomerCustomFieldsForOrganisationRequest),
        fork(watchGetTaskInvoiceCustomFieldsForOrganisationRequest),
        fork(watchTaskActionPhoneCallTaskRequest),
        fork(watchTaskActionOrganisationPhoneCallTaskRequest),
        fork(watchGetActionedPhoneCallDataRequest),
        fork(watchTasksFetchedForOrganisationReceived),
        fork(watchActionOrganisationPaymentRALinkTaskRequest),
        fork(watchTasksFetchedForOrganisationReceived),
        fork(watchActionOrganisationRequestRATaskRequest),
        fork(watchGetOrganisationTasksHistoryRequest),
        fork(watchGetOrganisationTaskHistoryNotificationDataRequest),
        fork(watchGetOrganisationTaskHistoryNotificationInvoicesRequest),
        fork(watchTaskActionCancelPaymentPlanRequest),
        fork(watchGetActionedCancelPaymentPlanDetailsRequest)
    ]);
}

export default tasksSaga;